import { useCallback, useEffect, useRef, useState } from 'react';

export function usePlayback(frameCount: number, fps: number) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);

  const stop = useCallback(() => {
    window.clearInterval(timerRef.current);
    timerRef.current = undefined;
    setPlaying(false);
  }, []);

  useEffect(() => {
    if (!playing || frameCount === 0) return;
    timerRef.current = window.setInterval(() => {
      setIndex((current) => {
        if (current + 1 >= frameCount) {
          window.clearInterval(timerRef.current);
          timerRef.current = undefined;
          setPlaying(false);
          return 0;
        }
        return current + 1;
      });
    }, 1000 / fps);
    return () => window.clearInterval(timerRef.current);
  }, [playing, frameCount, fps]);

  useEffect(() => {
    if (index >= frameCount) setIndex(Math.max(0, frameCount - 1));
  }, [frameCount, index]);

  const playPause = useCallback(() => {
    if (!frameCount) return;
    setPlaying((value) => !value);
  }, [frameCount]);

  const restart = useCallback(() => {
    stop();
    setIndex(0);
  }, [stop]);

  const choose = useCallback((next: number) => {
    stop();
    setIndex(next);
  }, [stop]);

  return { index, playing, playPause, restart, choose, stop };
}
